import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../../api/axiosConfig';

const SOCKET_URL = API_BASE_URL;

/**
 * GlobalSocketListener
 *
 * Invisible component — mounts once on login.
 * Connects to the Socket.IO server, registers the user,
 * and fires OS desktop notifications when new_notification arrives.
 *
 * Respects the user's desktop_enabled preference fetched fresh
 * from /api/notifications/preferences on every new notification.
 * This means changes made on the Settings page take effect
 * immediately without requiring a page reload.
 */
const GlobalSocketListener = ({ userId }) => {
  const navigate = useNavigate();
  // Cache the latest desktop_enabled value so we don't hammer the API
  const desktopEnabledRef = useRef(true);
  const prefFetchedRef    = useRef(false);

  // Fetch preferences once on mount (and whenever userId changes)
  useEffect(() => {
    if (!userId) return;

    const fetchPrefs = async () => {
      try {
        const res = await api.get('/notifications/preferences');
        if (res.data) {
          desktopEnabledRef.current = res.data.desktop_enabled !== false;
        }
      } catch {
        // Silently fall back to enabled
      } finally {
        prefFetchedRef.current = true;
      }
    };

    fetchPrefs();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Ask for OS permission on first login
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      console.log('[Socket] Connected:', socket.id);
      socket.emit('register_user', userId);
    });

    socket.on('new_notification', (data) => {
      console.log('[Socket] Real-time notification received:', data);

      // Fire OS desktop notification only if the user has it enabled
      if (
        desktopEnabledRef.current &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const osNotification = new Notification(data.title, {
          body: data.message,
          // icon: '/logo.png',
          tag: String(data.id),
          requireInteraction: false,
        });

        osNotification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          if (data.link_url) navigate(data.link_url);
          osNotification.close();
        };
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, navigate]);

  return null;
};

export default GlobalSocketListener;